<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Fragments';
    let nextLink= '/Documentation/Defining_an_Editor/The_Parser_Projection';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="styling-1"
  bind:intersecting={visibleById["styling-1"]}
  onIntersectChange={(visible) => setVisible("styling-1", visible)}
>Styling</SectionComponent>
<p>The web app and/or the editor can be styled using either SCSS or CSS. </p>
<SectionComponent tag="h1" 
  id="styling-the-web-app-2"
  bind:intersecting={visibleById["styling-the-web-app-2"]}
  onIntersectChange={(visible) => setVisible("styling-the-web-app-2", visible)}
>Styling the Web App</SectionComponent>
<p>The provided <a
  href="/Documentation/Overview/Getting_Started#a-minimal-web-app-and-server-6"
>web app</a>
is built using the <a href="https://flowbite-svelte.com/" target="_blank">Flowbite Svelte</a> UI library.
This library uses <a href="https://tailwindcss.com/" target="_blank">Tailwind</a>. We have provided a CSS file that includes the required styling, which is called
<code>webapp-styles.css</code>. It imports the base Tailwind styles, as well as styles for the core-svelte and the webapp libraries.
Any styles that you want to define should be imported last, in order for them to overwrite the default styles.
In the provided CSS file we have already included imports for two entries:</p>
<ol>
<li>a file for any styling of external components</li>
<li>a file for changing the theme of the webapp.</li>
</ol>
<pre class="language-scss"><code class="language-scss">// Styling/webapp-styles.css

/* Import the standard tailwind styles */
@import 'tailwindcss';

/* Import all styles from core-svelte */
@import '../../../node_modules/@freon4dsl/core-svelte/src/lib/styles/freon.css';
@import '../../../node_modules/@freon4dsl/core-svelte/src/lib/styles/freon-light.css';
@import '../../../node_modules/@freon4dsl/core-svelte/src/lib/styles/freon-tw-dark.css';
@import '../../../node_modules/@freon4dsl/core-svelte/src/lib/styles/freon-color-definitions.css';

/* Import all styles from the webapp library */
@import '../../../node_modules/@freon4dsl/weblib-flowbite/dist/styles/lib-styles.css';

/* Import local styles and overrides */
@import './theme-colors-override.css';
@import './externals-styles.css';
</code></pre>
<p>Run the following command to make these files available. If you don’t modify any of the styles, a single
run is enough. However, if you do make changes, rerun this command.</p>
<pre class="language-bash"><code class="language-bash">  npm run styles   # Needed to generate the runtime CSS files. A single run will suffice.</code></pre>
<SectionComponent tag="h2" 
  id="changing-the-theme-3"
  bind:intersecting={visibleById["changing-the-theme-3"]}
  onIntersectChange={(visible) => setVisible("changing-the-theme-3", visible)}
>Changing the Theme</SectionComponent>
<p>To change the theme colors you need to edit the colors in the <code>theme-colors-override.css</code> file.
This file contains various sets of colors that are used in the webapp dark and light modes, and in the editor dark and light modes.</p>
<pre class="language-scss"><code class="language-scss">// Styling/theme-colors-override.css

:root &#123;
    /* Colors for the Flowbite webapp Light mode  */
    --color-light-base-50:  #FFFCF2;
    --color-light-base-100: #FFF1CC;
    --color-light-base-150: #FFE8A3;
    --color-light-base-200: #FFDD7A;
    --color-light-base-250: #FFD24D;
    --color-light-base-300: #FFC61A;
    --color-light-base-350: #FFC61A;
    --color-light-base-400: #E6AF00;
    --color-light-base-500: #E6AF00;
    --color-light-base-600: #BF9200;
    --color-light-base-700: #997500;
    --color-light-base-800: #735900;
    --color-light-base-900: #4D3D00;

    /* Colors for the Flowbite webapp Light mode accent */
    --color-light-accent-50:  #EAF4FF;
    --color-light-accent-100: #CFE6FF;
    --color-light-accent-200: #B3D7FF;
    --color-light-accent-300: #99C8FF;
    --color-light-accent-400: #7AB6FF;
    --color-light-accent-500: #5AA3FF;
    --color-light-accent-600: #3F8CFF;
    --color-light-accent-700: #2F6FCE;
    --color-light-accent-800: #22539B;
    --color-light-accent-900: #173A6B;

    /* Colors for the Freon editor Light mode  */
    --color-light-freon-50:  #FFFCF2;
    --color-light-freon-100: #FFF1CC;
    --color-light-freon-150: #FFE8A3;
    --color-light-freon-200: #FFDD7A;
    --color-light-freon-250: #FFD24D;
    --color-light-freon-300: #FFC61A;
    --color-light-freon-350: #FFC61A;
    --color-light-freon-400: #E6AF00;
    --color-light-freon-500: #E6AF00;
    --color-light-freon-600: #BF9200;
    --color-light-freon-700: #997500;
    --color-light-freon-800: #735900;
    --color-light-freon-900: #4D3D00;
    --color-light-freon-error:    #FF4C4C;       /* unchanged */
    --color-light-freon-editable: #FFE8A3;       /* was orange-150 → yellow-150 */
    --color-light-freon-optional: #E6AF00;       /* was orange-400/500 → yellow-400/500 */
    --color-light-freon-border:   #22539B;       /* was green-800 → blue-800 */
    --color-light-freon-dropdown: #2F6FCE;       /* was green-700 → blue-700 */
    --color-light-freon-button:   #CFE6FF;       /* was green-100 → blue-100 */

    /* Colors for the Flowbite webapp Dark mode */
    --color-dark-base-50:  #FAFBFF;
    --color-dark-base-100: #EAF4FF;
    --color-dark-base-150: #CFE6FF;
    --color-dark-base-200: #BFE0FF;
    --color-dark-base-250: #B3D7FF;
    --color-dark-base-300: #99C8FF;
    --color-dark-base-350: #7AB6FF;
    --color-dark-base-400: #5AA3FF;
    --color-dark-base-500: #3F8CFF;
    --color-dark-base-600: #2F6FCE;
    --color-dark-base-700: #22539B;
    --color-dark-base-800: #173A6B;
    --color-dark-base-900: #0F2746;

    --color-dark-accent-50:  #FFF1CC;
    --color-dark-accent-100: #FFE8A3;
    --color-dark-accent-200: #FFDD7A;
    --color-dark-accent-300: #FFD24D;
    --color-dark-accent-400: #FFC61A;
    --color-dark-accent-500: #E6AF00;
    --color-dark-accent-600: #BF9200;
    --color-dark-accent-700: #997500;
    --color-dark-accent-800: #735900;
    --color-dark-accent-900: #4D3D00;

    /* Colors for the Freon editor Dark mode */
    --color-dark-freon-50:  #FAFBFF;
    --color-dark-freon-100: #EAF4FF;
    --color-dark-freon-150: #CFE6FF;
    --color-dark-freon-200: #BFE0FF;
    --color-dark-freon-250: #B3D7FF;
    --color-dark-freon-300: #99C8FF;
    --color-dark-freon-350: #7AB6FF;
    --color-dark-freon-400: #5AA3FF;
    --color-dark-freon-500: #3F8CFF;
    --color-dark-freon-600: #2F6FCE;
    --color-dark-freon-700: #22539B;
    --color-dark-freon-800: #173A6B;
    --color-dark-freon-900: #0F2746;
    --color-dark-freon-error:    #FF4C4C;  /* unchanged */
    --color-dark-freon-editable: #FFE8A3;  /* yellow-150 */
    --color-dark-freon-optional: #E6AF00;  /* yellow-400/500 */
    --color-dark-freon-border:   #22539B;  /* blue-800 */
    --color-dark-freon-dropdown: #2F6FCE;  /* blue-700 */
    --color-dark-freon-button:   #7AB6FF;  /* blue-350 */

    /* Colors used in both light and dark mode (colorblind-safe version) */
    --color-light-freon-accent-error: #FF4C4C;         /* unchanged */
    --color-light-freon-accent-editable: #FFE8A3;      /* was #FFD0B3 → lighter yellow */
    --color-light-freon-accent-optional: #E6AF00;      /* was #DB6506 → deep yellow */
    --color-light-freon-border-green: #22539B;         /* was #47651A → strong blue */
    --color-light-freon-accent-dropdown: #2F6FCE;      /* was #5B7F20 → mid-blue */
    --color-light-freon-accent-button: #7AB6FF;        /* was #98CA32 → bright blue */
    --freon-light-accent-lightgreen: #EAF4FF;          /* was #e4f5e5 → very light blue */

    /* Colors used in dark mode (colorblind-safe version) */
    --color-dark-freon-accent-error: #FF4C4C;          /* unchanged */
    --color-dark-freon-accent-editable: #FFE8A3;       /* was #FFD0B3 → soft yellow */
    --color-dark-freon-accent-optional: #E6AF00;       /* was #DB6506 → deep yellow */
    --color-dark-freon-border-green: #22539B;          /* was #47651A → strong blue */
    --color-dark-freon-accent-dropdown: #2F6FCE;       /* was #5B7F20 → medium blue */
    --color-dark-freon-accent-button: #7AB6FF;         /* was #98CA32 → bright blue */
&#125;
</code></pre>
<SectionComponent tag="h2" 
  id="styling-the-editor-4"
  bind:intersecting={visibleById["styling-the-editor-4"]}
  onIntersectChange={(visible) => setVisible("styling-the-editor-4", visible)}
>Styling the Editor</SectionComponent>
<p>Because the minimal <a
  href="/Documentation/Overview/Getting_Started#a-minimal-web-app-and-server-6"
>web app</a>
is provided solely for your convenience, the styling of the editor is kept separate from the styling of the web
application. In the above <code>webapp-styles.css</code> file, you will notice that four CSS files from
the <code>@freon4dsl/core-svelte</code> package are imported. One includes all styling that does not concern colors (<code>freon.css</code>).
Two files define the colors for either the light or dark mode (<code>freon-light.css</code> and <code>freon-tw-dark.css</code>). If you are not
using <a href="https://tailwindcss.com/" target="_blank">Tailwind</a>, a file called <code>freon-plain-dark.css</code> provides all
dark mode styling in plain css. The fourth file defines colors such that they can be used to theme the web app and editor.</p>
<p>The comments and naming in each CSS file should (hopefully) indicate where each
style is being used. For example, the following styles are defined for horizontal and vertical lists.</p>
<pre class="language-scss"><code class="language-scss">// Styling/freon.css#L50-L66

.list-component-horizontal &#123;
    white-space: nowrap;
    display: grid;
    padding: 0px;
    border-width: 0pt;
    border-style: none;
    margin: 0px;
    box-sizing: border-box;
&#125;

.list-component-vertical &#123;
    padding: 0px;
    border-width: 0pt;
    border-style: none;
    margin: 1px;
    box-sizing: border-box;
&#125;</code></pre>
<p>To modify the styling, copy the files from the <code>@freon4dsl/core-svelte</code> package into your
styles folder and adjust the <code>@import</code> statement in <code>webapp-styles.scss</code> to include your own
files instead of the packaged ones. Then start changing the styles in your own
copies to your liking, however, take care when modifying the boolean and numeric controls, because they are based on
Material UI components.</p>
<p>The following components derive their colors from Material UI variables, which you can override using Freon-specific CSS variables:</p>
<ul>
<li>RadioComponent uses  “—mdc-theme-secondary” for its color, if you want to override this value set “—freon-boolean-radiobox-color”.</li>
<li>CheckComponent uses  “—mdc-theme-secondary”, if you want to override it, use “—freon-boolean-checkbox-color”.</li>
<li>SwitchComponent uses  “—mdc-theme-primary”, if you want to override it, set “—freon-boolean-switch-color”.</li>
<li>NumericSliderComponent uses  “—mdc-theme-primary”, if you want to override it, set “—freon-numeric-slider-color”.</li>
<li>InnerSwitchComponent uses  “—mdc-theme-primary”, if you want to override it, set “—freon-boolean-switch-color”.</li>
</ul>


<PrevNextSection {prevLink} {nextLink} />